import cc from "classcat"
import H2 from "components/atoms/H2"
import ScrollAnimate from "components/atoms/common/ScrollAnimate"

const Video = ({ src, poster = "" }) => (
  <video
    loop
    className="lg:relative lg:left-1/2 lg:h-full lg:max-w-none lg:-translate-x-2/4 3xl:h-[unset] 3xl:w-fit 3xl:max-w-full"
    autoPlay
    muted
    playsInline
    preload="auto"
    poster={poster}
  >
    <source src={src} type="video/mp4" />
  </video>
)

const SimpleWrapper = ({ children }) => <>{children}</>

type DividedProps = {
  title?: React.ReactNode
  Image?: (props?: any) => JSX.Element
  children: React.ReactElement
  theme: string
  reversed?: boolean
  fullHeight?: boolean
  listLength?: number
  noVerticalPadding?: boolean
  contentClassName?: string
  className?: string
  imageClassName?: string
  hideMobileImage?: boolean
  ImageContent?: (props?: any) => JSX.Element
  video?: string
  titleSize?: Size
  animateTitle?: boolean
  poster?: string
}

const Divided = ({
  title = "",
  Image,
  children,
  theme = "green",
  reversed = false,
  fullHeight = true,
  listLength = 1,
  noVerticalPadding = false,
  contentClassName = "",
  className = "",
  hideMobileImage = false,
  ImageContent,
  video,
  titleSize = "lg",
  imageClassName = "",
  animateTitle = false,
  poster = "",
}: DividedProps) => {
  const TitleWrapper = animateTitle ? ScrollAnimate : SimpleWrapper
  return (
    <div
      className={cc([
        "relative",
        className,
        {
          "bg-kkm-green": theme === "green",
          "bg-kkm-darkgreen": theme === "darkgreen",
          "bg-kkm-yellow": theme === "yellow",
          "bg-kkm-red": theme === "red",
          "bg-white": theme === "light" || theme === "lightyellow",
          "bg-zinc-800": theme === "dark" || theme === "darkyellow",
          "py-16 lg:py-24": !noVerticalPadding,
        },
      ])}
    >
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-start gap-y-12 lg:grid-cols-2">
          {/* Content */}
          <div
            className={cc([
              "lg:space-y-12",
              reversed ? "lg:ml-12" : "lg:mr-12",
              contentClassName,
              hideMobileImage ? "pt-10" : "",
            ])}
          >
            {title && (
              <TitleWrapper>
                <H2
                  size={titleSize}
                  className="m-0 mb-6 whitespace-pre-line text-left font-extrabold tracking-tight"
                  theme={theme}
                >
                  {title}
                </H2>
              </TitleWrapper>
            )}
            <div>{children}</div>
          </div>
          {/* Image */}
          <div
            className={cc([
              "order-first -mx-4 flex h-full sm:-mx-6 lg:mx-0",
              reversed
                ? "pr-4 sm:pr-6 lg:pr-0"
                : "pl-4 sm:pl-6 lg:order-last lg:pl-0",
              hideMobileImage ? "hidden md:block" : "",
            ])}
          >
            <div
              className={cc([
                "w-full lg:absolute lg:w-1/2 ",
                reversed ? "lg:left-0 lg:pr-12" : "lg:right-0 lg:pl-12",
                fullHeight
                  ? noVerticalPadding
                    ? "lg:inset-y-0"
                    : "lg:inset-y-24"
                  : "lg:top-24",
                {
                  "lg:h-[28rem] xl:h-[26rem]": !fullHeight && listLength === 3,
                  "lg:h-[32rem]": !fullHeight && listLength === 5,
                  "lg:h-[39rem]": !fullHeight && listLength === 6,
                },
                imageClassName,
              ])}
            >
              <div
                className={cc([
                  "relative isolate h-full overflow-hidden",
                  reversed ? "rounded-r-full" : "rounded-l-full",
                  video ? "" : "flex",
                ])}
              >
                {video ? (
                  <Video src={video} poster={poster} />
                ) : (
                  <Image
                    className={cc([
                      "w-full",
                      {
                        "border-kkm-green": theme === "green",
                        "border-white": theme === "light",
                        "border-zinc-800": theme === "dark",
                      },
                    ])}
                  />
                )}
                {ImageContent ? (
                  <div className="absolute inset-4 lg:hidden">
                    <ImageContent />
                  </div>
                ) : null}
              </div>
            </div>
            {ImageContent ? (
              <div className="hidden lg:block">
                <ImageContent />
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Divided
